import React, { useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { CircularProgress } from "@material-ui/core";
import cogoToast from "cogo-toast";

const useStyles = makeStyles({
  container: {
    marginTop: "12px",
  },
  dropzone: {
    border: "2px dashed #d1d5db",
    borderRadius: "12px",
    padding: "40px 24px",
    textAlign: "center",
    cursor: "pointer",
    transition: "all 0.2s ease",
    backgroundColor: "#fafbfc",
    "&:hover": {
      borderColor: "#3a6ed4",
      backgroundColor: "#f0f4ff",
    },
  },
  dropzoneActive: {
    borderColor: "#3a6ed4",
    backgroundColor: "#f0f4ff",
  },
  icon: {
    fontSize: "40px",
    marginBottom: "12px",
    display: "block",
  },
  title: {
    fontWeight: "600",
    fontSize: "15px",
    color: "#1a1a1a",
    marginBottom: "6px",
  },
  subtext: {
    fontSize: "13px",
    color: "#6c757d",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "40px 24px",
    border: "2px solid #e0e0e0",
    borderRadius: "12px",
    backgroundColor: "#fafbfc",
  },
  loadingText: {
    marginTop: "16px",
    fontSize: "14px",
    color: "#6c757d",
  },
  fileName: {
    marginTop: "8px",
    fontSize: "13px",
    color: "#3a6ed4",
    fontWeight: "500",
  },
});

const ACCEPTED_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const ACCEPTED_EXTENSIONS = ".pdf,.doc,.docx";

/**
 * Parse a PDF file and extract text content as HTML paragraphs.
 */
const parsePDF = async (file) => {
  const pdfjsLib = await import("pdfjs-dist/legacy/build/pdf");
  const pdfjsWorker = await import("pdfjs-dist/legacy/build/pdf.worker.entry");
  pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  let fullText = "";

  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const textContent = await page.getTextContent();
    const pageText = textContent.items.map((item) => item.str).join(" ");
    if (pageText.trim()) {
      fullText += `<p>${pageText.trim()}</p>\n`;
    }
  }

  return fullText || "<p></p>";
};

/**
 * Parse a DOCX file and convert to HTML.
 */
const parseDOCX = async (file) => {
  const mammoth = await import("mammoth");
  const arrayBuffer = await file.arrayBuffer();
  const result = await mammoth.convertToHtml({ arrayBuffer });

  if (result.messages && result.messages.length > 0) {
    console.warn("Mammoth warnings:", result.messages);
  }

  return result.value || "<p></p>";
};

const JDFileUploader = ({ onParsed, onCancel }) => {
  const classes = useStyles();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState("");
  const [isDragging, setIsDragging] = useState(false);

  const processFile = async (file) => {
    if (!file) return;

    // Validate file type
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["pdf", "doc", "docx"].includes(ext)) {
      cogoToast.error("Please upload a PDF, DOC, or DOCX file", {
        position: "top-center",
        hideAfter: 4,
      });
      return;
    }

    setFileName(file.name);
    setLoading(true);

    try {
      let htmlContent = "";

      if (ext === "pdf") {
        htmlContent = await parsePDF(file);
      } else if (ext === "docx") {
        htmlContent = await parseDOCX(file);
      } else if (ext === "doc") {
        // .doc has limited support with mammoth
        try {
          htmlContent = await parseDOCX(file);
        } catch {
          cogoToast.warn(
            "Legacy .doc format has limited support. Please convert to .docx for best results.",
            { position: "top-center", hideAfter: 5 },
          );
          setLoading(false);
          setFileName("");
          return;
        }
      }

      if (!htmlContent || htmlContent === "<p></p>") {
        cogoToast.warn(
          "Could not extract content from the file. It may be empty or image-based.",
          { position: "top-center", hideAfter: 4 },
        );
        setLoading(false);
        setFileName("");
        return;
      }

      cogoToast.success("File parsed successfully!", {
        position: "top-center",
        hideAfter: 3,
      });
      onParsed(htmlContent);
    } catch (error) {
      console.error("File parsing error:", error);
      cogoToast.error(
        "Failed to parse the file. Please try a different file or format.",
        { position: "top-center", hideAfter: 4 },
      );
      setFileName("");
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    processFile(file);
    // Reset input so same file can be re-selected
    e.target.value = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    processFile(file);
  };

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress size={36} style={{ color: "#3a6ed4" }} />
        <div className={classes.loadingText}>Parsing file...</div>
        {fileName && <div className={classes.fileName}>{fileName}</div>}
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <input
        type="file"
        ref={fileInputRef}
        accept={ACCEPTED_EXTENSIONS}
        onChange={handleFileSelect}
        style={{ display: "none" }}
      />
      <div
        className={`${classes.dropzone} ${
          isDragging ? classes.dropzoneActive : ""
        }`}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <span className={classes.icon}>📁</span>
        <div className={classes.title}>
          Click to browse or drag & drop your file
        </div>
        <div className={classes.subtext}>Supported: PDF, DOC, DOCX</div>
      </div>
    </div>
  );
};

export default JDFileUploader;
